// API Configuration for Sandhu Scientific E-commerce Backend
//
// Browser traffic uses the first-party /backend proxy so guest session cookies
// do not depend on cross-origin cookie policies. REACT_APP_FORCE_REMOTE_API is
// reserved for the build-time prerender server, which cannot provide that proxy.
// REACT_APP_API_BASE_URL remains available to the build-time prerender path.
const DEFAULT_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://new-api.sseaindia.com";
const PROXY_BASE_URL = "/backend";
const MEDIA_BASE_URL = process.env.REACT_APP_MEDIA_BASE_URL || DEFAULT_API_BASE_URL;
const isBrowser = typeof window !== "undefined";
const isLocalhost = isBrowser && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
const forceRemoteApi = process.env.REACT_APP_FORCE_REMOTE_API === "true";
const API_BASE_URL = isLocalhost && forceRemoteApi
  ? DEFAULT_API_BASE_URL
  : PROXY_BASE_URL;

// API Endpoints Configuration
export const API_ENDPOINTS = {
  // Base URL
  BASE_URL: API_BASE_URL,
  PROXY_BASE_URL,
  MEDIA_BASE_URL: MEDIA_BASE_URL,

  // Authentication APIs
  AUTH: {
    SEND_OTP: '/auth/sendOtp/',
    VERIFY_OTP: '/auth/verifyOtp/',
    PROVIDE_PASSWORD: '/auth/providePassword/',
    RESEND_OTP: '/auth/resendOtp/',
    LOGIN: '/auth/login/',
    SEND_LOGIN_OTP: '/auth/sendLoginOtp/',
    VERIFY_LOGIN_OTP: '/auth/verifyLoginOtp/',
    PROFILE: '/auth/profile/',
    CHANGE_PASSWORD: '/auth/changePassword/',
    DELETE_ACCOUNT: '/auth/deleteAccount/',
    REFRESH_TOKEN: '/auth/token/refresh/',
  },

  // Product APIs
  PRODUCTS: {
    LIST: '/api/products/',
    DETAIL: '/api/products', // Use with /{slug}/
    REVIEWS: '/api/products', // Use with /{slug}/reviews/
    FEATURED: '/api/products/featured/',
    FLAGSHIP: '/api/products/flagship/',
    SEARCH: '/api/products/', // Use with search params
  },

  // Category APIs
  CATEGORIES: {
    LIST: '/api/categories/',
    DETAIL: '/api/categories', // Use with /{id}/
  },

  // Product Groups APIs
  PRODUCT_GROUPS: {
    LIST: '/api/product-groups/',
    DETAIL: '/api/product-groups', // Use with /{slug}/
  },

  // Cover Image APIs
  COVER_IMAGES: {
    LIST: '/api/cover-images/',
    DETAIL: '/api/cover-images', // Use with /{id}/
    HIGHLIGHT: '/api/cover-images/highlight/',
    UPLOAD: '/api/cover-images/upload/', // Admin only
    UPDATE: '/api/cover-images', // Use with /{id}/update/ - Admin only
    DELETE: '/api/cover-images', // Use with /{id}/delete/ - Admin only
    STATS: '/api/cover-images/stats/', // Admin only
  },

  // Cart APIs
  CART: {
    GET: '/order/api/cart/',
    ADD_ITEM: '/order/api/cart/add_item/',
    UPDATE_ITEM: '/order/api/cart', // Use with /{id}/update_item/
    REMOVE_ITEM: '/order/api/cart', // Use with /{id}/remove_item/
    CLEAR: '/order/api/cart/',
    VALIDATE_CHECKOUT: '/order/api/cart/validate_checkout/',
    MERGE_GUEST: '/order/api/cart/merge_guest/',
  },

  // Wishlist APIs
  WISHLIST: {
    LIST: '/order/api/wishlist/',
    TOGGLE: '/order/api/wishlist/toggle/',
    MOVE_TO_CART: '/order/api/wishlist', // Use with /{id}/move_to_cart/
    MERGE_GUEST: '/order/api/wishlist/merge_guest/',
  },

  // Order APIs
  ORDERS: {
    CREATE: '/order/api/orders/create/',
    HISTORY: '/order/api/orders/history/',
    DETAIL: '/order/api/orders', // Use with /{order_number}/
  },

  // Payment APIs
  PAYMENT: {
    CREATE_ORDER: '/order/api/payment/create-order/',
    VERIFY: '/order/api/payment/verify/',
    FAILURE: '/order/api/payment/failure/',
    STATUS: '/order/api/payment/status', // Use with /{order_number}/
    WEBHOOK: '/order/webhooks/razorpay/',
  },

};

// Helper function to build full URL
export const buildApiUrl = (endpoint, params = {}) => {
  // Axios applies relative base URLs itself. Only pre-compose absolute URLs
  // for build-time consumers that call the production API directly.
  let url = /^https?:\/\//.test(API_BASE_URL)
    ? `${API_BASE_URL}${endpoint}`
    : endpoint;
  
  // Replace URL parameters
  Object.keys(params).forEach(key => {
    url = url.replace(`{${key}}`, params[key]);
  });
  
  return url;
};

// Native fetch does not apply Axios's baseURL; include the proxy prefix here.
export const buildFetchApiUrl = (endpoint) => `${API_BASE_URL}${endpoint}`;

// Helper function to build URL with query parameters
export const buildApiUrlWithQuery = (endpoint, queryParams = {}) => {
  const baseUrl = buildApiUrl(endpoint);
  const searchParams = new URLSearchParams();
  
  Object.keys(queryParams).forEach(key => {
    if (queryParams[key] !== null && queryParams[key] !== undefined && queryParams[key] !== '') {
      searchParams.append(key, queryParams[key]);
    }
  });
  
  const queryString = searchParams.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

// Common HTTP headers
export const API_HEADERS = {
  'Content-Type': 'application/json',
  'Accept': 'application/json',
};

// API response status codes
export const API_STATUS = {
  SUCCESS: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  METHOD_NOT_ALLOWED: 405,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  INTERNAL_SERVER_ERROR: 500,
};

export default API_ENDPOINTS;
